/** Parent-facing Year 7 path for Wilson's School. */

export const WILSONS_JOURNEY = {
  schoolId: 7,
  schoolName: "Wilson's School",
  kicker: "School guide",
  title: "What to do for Wilson's",
  lead:
    "A short parent guide: what the school is, how catchment works here, how to apply, exams, results, how places are ordered, and how this school sits next to nearby grammars.",
  heroStats: [
    { label: "GCSE rank", value: "#2" },
    { label: "Gender", value: "Boys" },
    { label: "Catchment", value: "Open" },
    { label: "Oxbridge offers", value: "37" },
    { label: "Area", value: "Sutton" },
  ],
  catchmentVisual: {
    kind: "open",
    aria: "Wilson's has no closed catchment ring",
    caption: "",
    labels: {
      school: "Wilson's",
      outside:
        "No closed catchment ring. Distance is only used later if two boys have the same test rank.",
    },
  },
  sources: [
    {
      label: "Year 7 — applying for a place",
      href: "https://www.wilsons.school/year-7-applying-for-a-place/",
    },
    {
      label: "Admission criteria",
      href: "https://www.wilsons.school/admissions-criteria/",
    },
    {
      label: "Oxbridge offers",
      href: "https://www.wilsons.school/oxbridge-offers-received/",
    },
  ],
  steps: [
    {
      id: "overview",
      title: "1. Overview",
      summary: "What Wilson's is, in one minute",
      paragraphs: [
        "Wilson's School is a selective state grammar school for boys in Wallington, in the London Borough of Sutton. It is not fee-paying. Boys take entrance tests in Year 6 for a place in Year 7.",
        "There are 186 Year 7 places in a typical published year. Places go to boys who pass the entrance process and then rank highly enough under the school's admission rules.",
        "Wilson's sits in the Sutton selective group. Boys sit a shared Selective Eligibility Test (SET), then a shared second-stage exam with Sutton Grammar School and Wallington County Grammar School.",
      ],
    },
    {
      id: "catchment",
      title: "2. Catchment check",
      summary: "No closed map ring — test rank comes first",
      paragraphs: [
        "Wilson's does not use a traditional catchment polygon that locks families in or out. In SchoolCatch it is an open school: any eligible boy can register for the tests and can be considered if he passes and names Wilson's on the common application form (CAF).",
        "What matters most here is marks order — also called rank order. After the tests, the school puts eligible boys in a list from highest combined score to lowest. Places follow that list, with a few special pathways mixed in.",
        "Living in Sutton or living closer to the school only helps if two boys have the same rank and there are not enough places left. Distance is a tie-break, not the main gate.",
        "When does the home address matter? Wilson's uses the boy's permanent home address for that distance tie-break. In practice, use the address you put on the CAF by the end of October. Because there is no closed catchment ring, you do not need to “be inside a map” by a special catchment date — but the address used for ranking ties should be the real permanent home at application time.",
      ],
      example: {
        title: "Example",
        kind: "open",
        body:
          "Boy A and Boy B both pass and name Wilson’s. B lives much closer, but A has the higher score — so A is still offered first. Distance only separates them if their ranks are the same and places are short.",
      },
      showCatchmentVisual: true,
      points: [
        "No closed catchment boundary on the map",
        "Rank order = higher test score usually comes before lower score",
        "Sutton / distance only separates equal ranks",
        "Home address for ties: permanent home on the CAF (end of October)",
      ],
    },
    {
      id: "apply",
      title: "3. How to apply",
      summary: "Two forms, in the right order",
      paragraphs: [
        "Dates below are for Year 7 entry in September 2027 (from Wilson's published key dates). Always re-check the school site if you are applying for a later year.",
        "There are two separate applications. Missing either one usually means Wilson's cannot offer a place, even if your son sits well in the exams.",
        "First, register him online for the Selective Eligibility Test. The same registration form is shared across the Sutton selective schools. For 2027 entry, registration opens 1 May 2026 and closes at midnight on 31 July 2026. Late registrations are not accepted. Access-arrangement requests have an earlier deadline of 12 June 2026.",
        "Second, when he is in Year 6, complete your home local authority's CAF and name Wilson's in your true preference order by 31 October 2026. If Wilson's is not named on the CAF, the school cannot consider him.",
      ],
      timeline: [
        { when: "1 May 2026", what: "SET registration opens (2027 entry)" },
        { when: "12 June 2026", what: "Access arrangements deadline" },
        { when: "31 July 2026", what: "SET registration closes (midnight)" },
        { when: "15 Sept 2026", what: "Selective Eligibility Test" },
        { when: "3 Oct 2026", what: "Boys' second-stage exam if invited" },
        { when: "10 Oct 2026", what: "Wilson's music / sport aptitude tests" },
        { when: "31 Oct 2026", what: "CAF deadline — Wilson's must be named" },
        { when: "1 March 2027", what: "National Offer Day" },
      ],
    },
    {
      id: "exams",
      title: "4. Exams and preparation",
      summary: "What he sits, and how to prepare calmly",
      paragraphs: [
        "Stage 1 is the SET: English and Maths. A strong enough result invites him to the second stage. Stage 2 is a shared English and Maths exam with Sutton Grammar and Wallington County Grammar. One sitting feeds all three schools, but each school combines scores differently.",
        "One SET registration covers the joint Sutton selective group: Wilson’s, Sutton Grammar, Wallington County Grammar, Wallington High School for Girls, Nonsuch High School for Girls, and Greenshaw High School. Boys who pass are invited to the shared boys’ second stage (Wilson’s / Sutton Grammar / Wallington County Grammar). Girls use the girls’ second-stage path at Nonsuch / Wallington High. Greenshaw uses the SET without that second stage. You still have to name each school you want on the CAF — the test alone is not an application.",
        "Optional Wilson's-only sport and music aptitude tests open small aptitude pathways. They are not required for a normal place.",
        "Prepare with careful Year 5–6 English and Maths under timed conditions. Read the school's exam-day guidance for the year you are entering, and gather access-arrangement evidence early if needed.",
      ],
      points: [
        "SET — English and Maths (shared registration)",
        "Boys’ second stage shared with Sutton Grammar and Wallington County Grammar",
        "Optional Wilson’s sport and music aptitude tests",
      ],
    },
    {
      id: "place-order",
      title: "5. How places are ordered",
      summary: "The offer list, step by step",
      paragraphs: [
        "Once boys are eligible after the tests, Wilson's does not pick at random. It follows a published place order. Think of it as a queue with a few special lanes, then a main lane ordered by test score.",
        "EHCP and looked-after children are handled first. Then small sets of places can go to free-school-meals, sport aptitude, and music aptitude routes. Everyone else is ordered by marks — highest score first.",
        "If two boys have the same score, Sutton addresses are taken before addresses outside Sutton, and closer homes beat further ones.",
      ],
      placeOrder: [
        { title: "EHCP / looked-after", detail: "Handled at the front of the list under the published rules" },
        { title: "Free school meals places", detail: "A small set of places, still ordered by test score" },
        { title: "Sport aptitude places", detail: "Only if he cleared the sport aptitude benchmark" },
        { title: "Music aptitude places", detail: "Only if he cleared the music aptitude benchmark" },
        { title: "Everyone else by marks", detail: "Highest combined entrance score first" },
        { title: "Same score?", detail: "Sutton first, then closer distance to the school" },
      ],
    },
    {
      id: "results",
      title: "6. Results",
      summary: "Where Wilson's sits among grammars",
      paragraphs: [
        "On GCSEs, Wilson's sits right at the top of the grammar range — close to the highest Attainment 8 scores we hold, and well above the grammar-school average.",
        "At A level it is the same story: an A-grade average near the top of the grammar pack.",
        "The school has also published a large Oxbridge offer count. That figure comes from Wilson's itself, not from the Department for Education destinations release.",
      ],
      facts: [
        { label: "GCSE rank", value: "#2" },
        { label: "GCSE Attainment 8", value: "86.9" },
        { label: "A level average", value: "A" },
        { label: "Oxbridge offers", value: "37" },
      ],
    },
    {
      id: "allocation",
      title: "7. Places and demand",
      summary: "First exam funnel, then offer day",
      paragraphs: [
        "Start with the first exam, not Offer Day. The Selective Eligibility Test is shared across the Sutton group. For 2026 entry, more than six thousand children sat that first paper. Only a minority of boys then reach Wilson's own second stage — in recent years that second-stage group has been around a thousand boys.",
        "So the first exam is a wide gate that narrows quickly. Passing it only gets him into the next room; it does not mean a Wilson's place.",
        "On National Offer Day itself, far more families name Wilson's than there are places. Offers go to boys who are still eligible after both stages and who rank high enough.",
      ],
      facts: [
        { label: "SET sitters (2026)", value: "6,576" },
        { label: "First preferences", value: "413" },
        { label: "Offers", value: "215" },
        { label: "All preferences", value: "771" },
      ],
    },
    {
      id: "nearby",
      title: "8. Nearby schools",
      summary: "How Wilson's sits beside local grammars",
      paragraphs: [
        "Wilson's shares the Sutton selective testing path with nearby grammars. Wallington County Grammar and Sutton Grammar share the boys' second-stage exam. Wallington High and Nonsuch are girls' schools on the shared SET registration side.",
        "On GCSE Attainment 8, Wilson's sits above those nearby peers in the latest set we hold. That does not decide admissions — it is just a useful local comparison while you shortlist.",
      ],
      nearby: [
        { name: "Wallington County Grammar School", detail: "About 2 miles · shared boys' second stage · lower GCSE Attainment 8 than Wilson's" },
        { name: "Sutton Grammar School", detail: "About 4 miles · shared boys' second stage · strong results, a little below Wilson's" },
        { name: "Wallington High School for Girls", detail: "About 2 miles · shared SET registration · girls' grammar" },
        { name: "Nonsuch High School for Girls", detail: "About 7 miles · shared SET registration · girls' grammar" },
      ],
    },
    {
      id: "admission",
      title: "9. Offer day",
      summary: "From the list to March",
      paragraphs: [
        "National Offer Day is at the start of March. By then the school has already built its eligible list and applied the place order above.",
        "Always re-check Wilson's own admissions pages for the year you are entering — dates and small rules can move.",
      ],
    },
  ],
};
